import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export type Pan = { x: number; y: number };
export type CenterMode = 'center' | 'top';

type UsePanZoomOptions = {
  /** Outer clipping viewport (fixed size, overflow-hidden). Receives the events. */
  viewportRef: RefObject<HTMLDivElement | null>;
  /** Inner transform layer that holds the tree. Sized to its natural content. */
  contentRef: RefObject<HTMLDivElement | null>;
  minScale?: number;
  maxScale?: number;
  /** Additive step for the +/- buttons. */
  step?: number;
};

/**
 * Transform-based pan + zoom for the genealogy canvas. The viewport clips
 * (overflow-hidden); the content layer is moved with
 * `transform: translate(pan) scale(scale)` and `transform-origin: 0 0`. Because
 * panning is a transform (not scroll), the canvas is effectively infinite — you
 * can drag past any edge without hitting a scroll wall.
 *
 * Interactive gestures (drag, wheel, pinch) apply immediately; programmatic
 * moves (buttons, reset, fit, centerOn) animate via the content layer's CSS
 * transition, which is disabled while `isInteracting` is true.
 */
export function usePanZoom({
  viewportRef,
  contentRef,
  minScale = 0.4,
  maxScale = 2,
  step = 0.2,
}: UsePanZoomOptions) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
  const [isInteracting, setIsInteracting] = useState(false);

  // Refs mirror state so the imperative pointer/wheel handlers read current
  // values without the effect re-subscribing on every render.
  const scaleRef = useRef(scale);
  const panRef = useRef(pan);
  scaleRef.current = scale;
  panRef.current = pan;

  const clamp = useCallback(
    (s: number) => Math.min(maxScale, Math.max(minScale, s)),
    [minScale, maxScale],
  );

  // Re-enable the CSS transition shortly after the last interactive gesture, so
  // a wheel/pinch burst tracks the fingers/cursor with no lag but a subsequent
  // button press still animates.
  const interactTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bumpInteracting = useCallback(() => {
    setIsInteracting(true);
    if (interactTimer.current) clearTimeout(interactTimer.current);
    interactTimer.current = setTimeout(() => setIsInteracting(false), 180);
  }, []);
  useEffect(() => () => {
    if (interactTimer.current) clearTimeout(interactTimer.current);
  }, []);

  // Zoom to an absolute target scale, keeping the given screen focal point
  // (viewport-local px) fixed. With origin 0 0 and screen = pan + scale*content,
  // the fixed-focal invariant gives: pan1 = F - (s1/s0)*(F - pan0).
  const zoomTo = useCallback(
    (targetScale: number, focal?: Pan, immediate = false) => {
      const el = viewportRef.current;
      const s0 = scaleRef.current;
      const s1 = clamp(targetScale);
      if (s1 === s0) return;
      const F = focal ?? { x: (el?.clientWidth ?? 0) / 2, y: (el?.clientHeight ?? 0) / 2 };
      const p0 = panRef.current;
      const ratio = s1 / s0;
      if (immediate) bumpInteracting();
      setPan({ x: F.x - ratio * (F.x - p0.x), y: F.y - ratio * (F.y - p0.y) });
      setScale(s1);
    },
    [clamp, viewportRef, bumpInteracting],
  );

  /** Zoom in/out by `delta` (animated — used by the +/- buttons). */
  const zoomBy = useCallback(
    (delta: number, focal?: Pan) => {
      zoomTo(scaleRef.current + delta, focal, false);
    },
    [zoomTo],
  );

  /**
   * Position `el` in the viewport. mode 'center' → node centered on both axes;
   * mode 'top' → horizontally centered with its top edge near the top (used for
   * the root, so a tree grows downward from the top). Animated.
   */
  const centerOn = useCallback(
    (el: Element, opts?: { mode?: CenterMode; scale?: number; topPad?: number }) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const mode = opts?.mode ?? 'center';
      const topPad = opts?.topPad ?? 24;
      const s0 = scaleRef.current;
      const s1 = clamp(opts?.scale ?? s0);
      const V = viewport.getBoundingClientRect();
      const N = el.getBoundingClientRect();
      const p0 = panRef.current;
      // Node anchor in viewport-local coords at the current scale s0.
      const nodeLeft = N.left - V.left;
      const nodeTop = N.top - V.top;
      const anchorX = nodeLeft + N.width / 2;
      const anchorY = mode === 'top' ? nodeTop : nodeTop + N.height / 2;
      // Content-local point under the anchor.
      const px = (anchorX - p0.x) / s0;
      const py = (anchorY - p0.y) / s0;
      // Desired screen target.
      const Tx = V.width / 2;
      const Ty = mode === 'top' ? topPad : V.height / 2;
      setPan({ x: Tx - s1 * px, y: Ty - s1 * py });
      setScale(s1);
    },
    [clamp, viewportRef],
  );

  /** Reset to 100% and recenter the root at the top. */
  const reset = useCallback(() => {
    const rootEl = viewportRef.current?.querySelector('[data-slot-id]');
    if (rootEl) centerOn(rootEl, { mode: 'top', scale: 1 });
    else {
      setScale(1);
      setPan({ x: 0, y: 0 });
    }
  }, [centerOn, viewportRef]);

  /** Scale so the whole tree fits the viewport, then center it. */
  const fit = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const s0 = scaleRef.current;
    const rect = content.getBoundingClientRect();
    const naturalW = rect.width / s0;
    const naturalH = rect.height / s0;
    if (naturalW <= 0 || naturalH <= 0) return;
    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    const pad = 32;
    const s1 = clamp(Math.min((vw - pad) / naturalW, (vh - pad) / naturalH));
    setScale(s1);
    setPan({
      x: (vw - naturalW * s1) / 2,
      y: naturalH * s1 <= vh ? (vh - naturalH * s1) / 2 : pad / 2,
    });
  }, [clamp, viewportRef, contentRef]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const pointers = new Map<number, Pan>();
    let panStart: Pan | null = null;
    let panOrigin: Pan = { x: 0, y: 0 };
    let moved = false;
    let pinchStartDist = 0;
    let pinchStartScale = 1;

    const isInteractive = (t: EventTarget | null) =>
      t instanceof Element && !!t.closest('button, input, select, a, [data-interactive]');

    const onPointerDown = (e: PointerEvent) => {
      // Let cards, carets, interactive seats and controls handle their own clicks.
      if (isInteractive(e.target)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        panStart = { x: e.clientX, y: e.clientY };
        panOrigin = panRef.current;
        moved = false;
        el.setPointerCapture?.(e.pointerId);
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        if (a && b) {
          pinchStartDist = Math.hypot(a.x - b.x, a.y - b.y);
          pinchStartScale = scaleRef.current;
          panStart = null; // suspend single-pointer pan during pinch
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        if (a && b && pinchStartDist > 0) {
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          const rect = el.getBoundingClientRect();
          const midX = (a.x + b.x) / 2 - rect.left;
          const midY = (a.y + b.y) / 2 - rect.top;
          zoomTo(pinchStartScale * (dist / pinchStartDist), { x: midX, y: midY }, true);
        }
        return;
      }

      if (!panStart) return;
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      if (!moved && Math.hypot(dx, dy) < 4) return; // threshold: preserve clicks
      if (!moved) {
        moved = true;
        setIsInteracting(true);
        el.style.cursor = 'grabbing';
      }
      setPan({ x: panOrigin.x + dx, y: panOrigin.y + dy });
    };

    const endPointer = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      el.releasePointerCapture?.(e.pointerId);
      if (pointers.size < 2) pinchStartDist = 0;
      if (pointers.size === 1) {
        const [pt] = [...pointers.values()];
        if (pt) {
          panStart = { x: pt.x, y: pt.y };
          panOrigin = panRef.current;
          moved = false;
        }
      } else if (pointers.size === 0) {
        panStart = null;
        if (moved) setIsInteracting(false);
        moved = false;
        el.style.cursor = 'grab';
      }
    };

    const onWheel = (e: WheelEvent) => {
      // Only hijack the wheel for zoom when Ctrl/Cmd is held (this is also how
      // browsers deliver a trackpad pinch). A plain wheel scrolls the page.
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const focal = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const factor = Math.exp(-e.deltaY * 0.0015);
      zoomTo(scaleRef.current * factor, focal, true);
    };

    el.style.cursor = 'grab';
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endPointer);
    el.addEventListener('pointercancel', endPointer);
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endPointer);
      el.removeEventListener('pointercancel', endPointer);
      el.removeEventListener('wheel', onWheel);
    };
  }, [viewportRef, zoomTo]);

  return { scale, pan, isInteracting, reset, zoomBy, fit, centerOn, step };
}
